import type { PropertyDTO } from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import { PageActions } from "./page-actions"
import { TopBarBreadcrumbs } from "./top-bar-breadcrumbs"

interface Props {
	property: PropertyDTO
	/**
	 * Present only inside a project — adds the second breadcrumb
	 * ("property name > project name") linking to the project root.
	 */
	project?: { id: string; name: string }
	onShareClick: () => void
	/**
	 * Members-chat rail integration. Optional so subpages that have no
	 * shared-chat surface (edit, transfer) can mount the top bar without
	 * the toggle button.
	 */
	chatRailOpen?: boolean
	chatUnreadCount?: number
	onChatRailToggle?: () => void
	/** Toggle label override — "Property chat" / "Project chat" per context. */
	chatToggleLabel?: string
}

/**
 * Mobile-only fixed bar (ZPL-353): breadcrumbs on the left, the contextual
 * action cluster on the right. On md+ the bar is gone — context comes from
 * the in-page editorial header, section navigation from the rail, and the
 * same PageActions cluster renders at the page header's top-right.
 */
export function PropertyTopBar({
	property,
	project,
	onShareClick,
	chatRailOpen,
	chatUnreadCount,
	onChatRailToggle,
	chatToggleLabel,
}: Props) {
	return (
		<header className="md:hidden fixed top-7 left-0 right-0 h-12 z-30 bg-card border-b border-border flex items-center justify-between gap-2 px-3">
			<div className="flex items-center justify-start min-w-0">
				<TopBarBreadcrumbs
					crumbs={[
						{ label: property.name, to: webPaths.property(property.id) },
						...(project
							? [
									{
										label: project.name,
										to: webPaths.project(property.id, project.id),
									},
								]
							: []),
					]}
				/>
			</div>
			<PageActions
				property={property}
				onShareClick={onShareClick}
				chatRailOpen={chatRailOpen}
				chatUnreadCount={chatUnreadCount}
				onChatRailToggle={onChatRailToggle}
				chatToggleLabel={chatToggleLabel}
				className="shrink-0"
			/>
		</header>
	)
}
